import { type Editor } from '@tiptap/react';
import { BookOpen, FlaskConical, Image, ListOrdered, Table } from 'lucide-react';
import * as React from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- Lib ---
import { isNodeInSchema } from '@/lib/tiptap-utils';
import { isSelectionTopNode } from '@/lib/zy-tiptap-utils';

// --- Table insert ---
import { TableInsertGridPanel } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-trigger-button/table-insert-grid-panel';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/tiptap-editor/tiptap-ui-primitive/popover';

import type { ZyExampleType } from '../../tiptap-extension/zyexample-extension/zyexample-extension';

export interface ZyExampleOption {
    type: ZyExampleType;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    command?: (editor: Editor) => void;
    opensTableInsert?: boolean;
}

export const zyExampleOptions: ZyExampleOption[] = [
    {
        type: 'example',
        label: 'zyExample',
        icon: BookOpen,
        command: (editor: Editor) => {
            editor.commands.insertZyExample('example', {});
        },
    },
    {
        type: 'figure',
        label: 'zyFigure',
        icon: Image,
        command: (editor: Editor) => {
            editor.commands.insertZyExample('figure', {});
        },
    },
    {
        type: 'enum',
        label: 'zyEnum',
        icon: ListOrdered,
        command: (editor: Editor) => {
            editor.commands.insertZyExample('enum', {});
        },
    },
    {
        type: 'table',
        label: 'zyTable',
        icon: Table,
        opensTableInsert: true,
    },
    {
        type: 'try',
        label: 'zyTry',
        icon: FlaskConical,
        command: (editor: Editor) => {
            editor.commands.insertZyExample('try', {});
        },
    },
];

export interface ZyExampleMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor;
    /**
     * The example types to display in the dropdown.
     */
    types?: ZyExampleType[];

    onOpenChange?: (isOpen: boolean) => void;
}

export function canInsertZyExample(editor: Editor | null): boolean {
    if (!editor) {
        return false;
    }

    if (!isNodeInSchema('zyExample', editor)) {
        return false;
    }

    if (!isSelectionTopNode(editor)) {
        return false;
    }

    return true;
}

export function shouldShowZyExampleMenu({ editor, zyExampleInSchema }: { editor: Editor | null; zyExampleInSchema: boolean }): boolean {
    if (!zyExampleInSchema || !editor) {
        return false;
    }
    return true;
}

export function useZyExampleMenuState(editor: Editor | null) {
    const [isOpen, setIsOpen] = React.useState(false);

    const zyExampleInSchema = isNodeInSchema('zyExample', editor);

    const canInsert = canInsertZyExample(editor);

    const handleOpenChange = React.useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
        setIsOpen(open);
        callback?.(open);
    }, []);

    return {
        isOpen,
        setIsOpen,
        zyExampleInSchema,
        canInsert,
        handleOpenChange,
    };
}

export function ZyExampleMenu({ editor: providedEditor, onOpenChange, ...props }: ZyExampleMenuProps) {
    const { editor } = useTiptapEditor(providedEditor);

    const { isOpen, zyExampleInSchema, canInsert, handleOpenChange } = useZyExampleMenuState(editor);
    const [insertPopoverOpen, setInsertPopoverOpen] = React.useState(false);

    const disabled = !canInsert;

    const show = React.useMemo(() => {
        return shouldShowZyExampleMenu({
            editor,
            zyExampleInSchema,
        });
    }, [editor, zyExampleInSchema]);

    const handleOnOpenChange = React.useCallback((open: boolean) => handleOpenChange(open, onOpenChange), [handleOpenChange, onOpenChange]);

    const handleExampleClick = React.useCallback(
        (example: ZyExampleOption) => {
            if (!editor) return;

            if (example.opensTableInsert) {
                handleOpenChange(false, onOpenChange);
                setInsertPopoverOpen(true);
                return;
            }

            example.command?.(editor);
            handleOpenChange(false, onOpenChange);
        },
        [editor, handleOpenChange, onOpenChange],
    );

    if (!show || !editor || !editor.isEditable) {
        return null;
    }

    return (
        <Popover
            open={insertPopoverOpen}
            onOpenChange={setInsertPopoverOpen}
        >
            <DropdownMenu
                open={isOpen}
                onOpenChange={handleOnOpenChange}
            >
                <PopoverAnchor asChild>
                    <DropdownMenuTrigger asChild>
                        <Button
                            type="button"
                            data-style="ghost"
                            data-active-state="off"
                            role="button"
                            tabIndex={-1}
                            aria-label="Insert zyExample"
                            tooltip="zyExample"
                            {...props}
                        >
                            <BookOpen className="tiptap-button-icon" />
                            <ChevronDownIcon className="tiptap-button-dropdown-small" />
                        </Button>
                    </DropdownMenuTrigger>
                </PopoverAnchor>

                <DropdownMenuContent>
                    <Card>
                        <CardBody>
                            <DropdownMenuGroup>
                                {zyExampleOptions.map(option => {
                                    return (
                                        <DropdownMenuItem
                                            key={option.type}
                                            asChild
                                            disabled={disabled}
                                        >
                                            <Button
                                                type="button"
                                                data-style="ghost"
                                                role="button"
                                                tabIndex={-1}
                                                disabled={disabled}
                                                onClick={() => handleExampleClick(option)}
                                            >
                                                <div className="flex-start flex w-full">
                                                    <option.icon className="mr-2 h-4 w-4" />
                                                    <span>{option.label}</span>
                                                </div>
                                            </Button>
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuGroup>
                        </CardBody>
                    </Card>
                </DropdownMenuContent>
            </DropdownMenu>

            <PopoverContent
                align="start"
                side="bottom"
            >
                <Card>
                    <CardBody>
                        <TableInsertGridPanel
                            editor={editor}
                            mode="zytable"
                            focusInputs={insertPopoverOpen}
                            onInserted={() => setInsertPopoverOpen(false)}
                        />
                    </CardBody>
                </Card>
            </PopoverContent>
        </Popover>
    );
}

export default ZyExampleMenu;
